// @flow
/* eslint-disable ta-code-conventions/has-matching-test */

import React, { type Node } from 'react';
import { NavigableList, NavigableListItem } from '@ta/list.navigable-list';
import type { AddressDisplay } from '@ta/common.typeahead/results/addressDisplay';
import TypeaheadJsonResult from '../results/TypeaheadJsonResult';
import NoResults from '../results/NoResults';
import type { TypeaheadResult } from '../TypeaheadResult';
import type { IconStyle } from '../results/BasicResult';

import Selector from './Selector';
import TypeaheadJsonFetcher, {
  type ResultType,
  standardSearchTypes,
} from '../fetchers/TypeaheadJsonFetcher';

type Props = {|
  /** The currently selected result. */
  selectedLocation: TypeaheadResult,
  /** Callback triggered when the selection changes. */
  selectionChanged: TypeaheadResult => void,
  /** Location types to be available in the search */
  locationTypes?: Array<ResultType>,
  /** A CSS class name to apply to the <input/> */
  className?: string,
  /** Callback for when the typeahead input gains focus. */
  onFocus?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Callback for when the typeahead input loses focus. */
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Optional placeholder */
  placeHolder?: string,
  /**
   * If true, clear the contents of the typeahead bar after selecting a value,
   * otherwise keep the selected value. Defaults to false.
   */
  clearValueOnSelection?: boolean,
  /** Optional goeId(s), for the case when already select Geo(s), and only search within this goe */
  parentids?: Array<number>,
  /** Optionally customize the icon color (green, or default gray) in the selector. */
  selectorGreenIcon?: boolean,
  /** Optionally customize the icon color (green, or default gray) in the result items. */
  resultIconStyle?: IconStyle,
  /** Optionally specify addressDisplay types. Type includes 'full_address' | 'parent_geos' | 'none', default is 'parent_geos' */
  addressDisplay?: AddressDisplay,
  trailingItem?: (
    isActive: boolean,
    onMouseMove: (event: SyntheticMouseEvent<HTMLElement>) => mixed,
  ) => Node,
  /** Optionally customize the max number of list result, default to be 8 */
  max?: number,
  /** If handleEmptyResult is true, a "No result" message will display when search returns no result */
  handleEmptyResult?: boolean,
  /** Callback to grab a ref to the text input */
  onRef?: (textInput: ?HTMLInputElement) => mixed,
  /** Default to be false. If true, dropdown will be closed when click outside the typeahead */
  closeDropdownOnClickOutside?: boolean,
|};

type State = {|
  textInputValue: string,
|};

/**
 * Component that lets you pick a location with typeahead support.
 * The location types can be specified via properties.
 */
class POISelector extends React.Component<Props, State> {
  static defaultProps = {
    locationTypes: standardSearchTypes,
    className: undefined,
    onFocus: undefined,
    onBlur: undefined,
    placeHolder: undefined,
    clearValueOnSelection: false,
    parentids: [],
    selectorGreenIcon: false,
    resultIconStyle: undefined,
    addressDisplay: 'parent_geos',
    trailingItem: undefined,
    max: 8,
    handleEmptyResult: false,
    onRef: () => {},
    closeDropdownOnClickOutside: true,
  };

  state = {
    textInputValue: this.props.selectedLocation.displayValue || '',
  };

  componentDidUpdate(prevProps: Props) {
    const arrayEquals = <T>(arr1: T[], arr2: T[]) =>
      arr1 === arr2 ||
      (arr1.length === arr2.length && arr1.every((item, index) => item === arr2[index]));

    if (!arrayEquals(prevProps.parentids || [], this.props.parentids || [])) {
      this.typeaheadJsonFetcher = this.getTypeaheadJsonFetcher();
    }
  }

  onResultSelected = (selectedResult: TypeaheadResult) => {
    this.props.selectionChanged(selectedResult);
    this.setState({
      textInputValue: this.props.clearValueOnSelection ? '' : selectedResult.displayValue || '',
    });
  };

  getTypeaheadJsonFetcher = () =>
    new TypeaheadJsonFetcher({
      interleaved: true,
      types: this.props.locationTypes,
      name_depth: 1,
      details: true,
      legacy_format: true,
      urlList: false,
      max: this.props.max,
      scoreThreshold: 0.8,
      parentids: this.props.parentids || [],
      scope: 1,
      beforeGeoId: 1,
      afterGeoId: 1,
    });

  textInputChanged = (newValue: string) => {
    this.setState({ textInputValue: newValue });
  };

  typeaheadJsonFetcher = this.getTypeaheadJsonFetcher();

  render() {
    const { trailingItem } = this.props;
    return (
      <Selector
        fetchers={[this.typeaheadJsonFetcher]}
        typeaheadTextInputValue={this.state.textInputValue}
        selectionChanged={this.onResultSelected}
        textInputChanged={this.textInputChanged}
        className={this.props.className}
        onFocus={this.props.onFocus}
        onBlur={this.props.onBlur}
        placeHolder={this.props.placeHolder}
        greenIcon={this.props.selectorGreenIcon}
        handleEmptyResult={this.props.handleEmptyResult}
        onRef={textInput => {
          if (this.props.onRef) {
            this.props.onRef(textInput);
          }
        }}
        closeDropdownOnClickOutside={this.props.closeDropdownOnClickOutside}
      >
        {(results, tracker, onSelection) => {
          let resultItems = [];
          if (results.typeaheadjson && !results.typeaheadjson.length) {
            resultItems = [
              (isActive, onMouseMove) => (
                <NavigableListItem onMouseMove={onMouseMove} key="result='no-result'">
                  <NoResults small />
                </NavigableListItem>
              ),
            ];
          } else {
            resultItems = [...(results.typeaheadjson || [])].map(r => (isActive, onMouseMove) => (
              <TypeaheadJsonResult
                addressDisplay={this.props.addressDisplay}
                result={r}
                tracker={tracker}
                onMouseMove={onMouseMove}
                onSelection={onSelection}
                active={isActive}
                key={`result=${r.value}`}
                iconStyle={this.props.resultIconStyle}
                includeIcon
              />
            ));
          }

          if (trailingItem) {
            resultItems.push(trailingItem);
          }

          return <NavigableList>{resultItems}</NavigableList>;
        }}
      </Selector>
    );
  }
}

export default POISelector;
